import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface GetHandler<T> {
  name: string;
  handle: (props?: any) => Promise<T>;
}

export interface GetDataOptions {
  enabled?: boolean;
  /** Override the default 60s staleTime — e.g. Infinity for static reference data. */
  staleTime?: number;
  /** Override the default true — set false for static data that shouldn't refetch on focus. */
  refetchOnWindowFocus?: boolean;
}

interface PostHandler<T, R> {
  handle: (data: T) => Promise<R>;
  name: string;
}

export interface PutHandler<T, R> {
  handle: (data: T) => Promise<R>;
  name: string;
  onSuccess?: (data: R) => void;
  onError?: (error: Error) => void;
}

interface DeleteHandler<T, R> {
  handle: (data: T) => Promise<R>;
  name: string;
}

export const useGetData = <T>(
  { handle, name }: GetHandler<T>,
  options?: GetDataOptions
) => {
  return useQuery<T>({
    queryKey: name.includes("_") ? name.split("_") : [name],
    queryFn: () => handle(),
    retry: 0,
    staleTime: options?.staleTime ?? 1 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: options?.enabled ?? true,
    refetchOnMount: true,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? true,
  });
};

/**
 * Basically this handle query use to handle
 * query in certain condition like onclick functionor function by certain condition
 * @param handle
 * @param name
 * @returns
 */
export const useGetConditionalData = <T>({ handle, name }: GetHandler<T>) => {
  return useQuery<T>({
    queryKey: [name],
    queryFn: () => handle(),
    refetchOnWindowFocus: false,
    enabled: false, // disable this query from automatically running,
  });
};

export const usePostData = <T, R>({ handle, name }: PostHandler<T, R>) => {
  return useMutation<R, Error, T>({
    mutationKey: [name],
    mutationFn: (data: T) => handle(data),
  });
};

export const usePutData = <T, R>({
  handle,
  name,
  onSuccess,
  onError,
}: PutHandler<T, R>) => {
  return useMutation<R, Error, T>({
    mutationKey: [name],
    mutationFn: (data: T) => handle(data),
    onSuccess,
    onError,
  });
};

export const useDeleteData = <T, R>({ handle, name }: DeleteHandler<T, R>) => {
  return useMutation<R, Error, T>({
    mutationKey: [name],
    mutationFn: (data: T) => handle(data),
  });
};

/**
 * Hook to invalidate (mark as stale) one or more query cache entries.
 *
 * **DIFFERENCE between `invalidateQueries` vs `refetchQueries`:**
 *
 * 1. **invalidateQueries** (✅ RECOMMENDED):
 *    - Marks query as "stale" (outdated)
 *    - Refetches ONLY if a component is using that query
 *    - Skips refetch for queries in background/inactive tabs
 *    - More efficient for multiple components
 *    - Auto-syncs all components using the same query
 *
 * 2. **refetchQueries** (❌ Less efficient):
 *    - Immediately fetches data from server (forced)
 *    - Fetches even if query is not currently in use
 *    - Also fetches for queries in background tabs
 *    - Wastes bandwidth and resources
 *
 * **WHEN TO USE `invalidateQueries`:**
 * - ✅ After mutations (create/update/delete)
 * - ✅ When data changes on backend
 * - ✅ Want to sync multiple components automatically
 * - ✅ Let React Query optimize when to fetch
 *
 * **WHEN TO USE `refetchQueries`:**
 * - ⚡ User clicks manual "Refresh" button
 * - ⚡ Polling with intervals
 * - ⚡ Need fresh data immediately without conditions
 *
 * @example
 * // Single query key
 * const invalidate = useInvalidateQueries();
 * await invalidate(["getRoles"]);
 *
 * @example
 * // Multiple query keys at once
 * const invalidate = useInvalidateQueries();
 * await invalidate([
 *   ["getRoles"],
 *   ["getRoleNames"],
 *   ["getUserRoles"]
 * ]);
 *
 * @example
 * // In component after mutation
 * const onSubmit = async (data) => {
 *   await createMutation.mutateAsync(data);
 *
 *   // Invalidate related queries
 *   await invalidate([
 *     ["getRoles"],      // Table will auto-refetch
 *     ["getRoleNames"]   // Dropdown will auto-refetch
 *   ]);
 * };
 *
 * @returns Function that accepts single key or array of keys
 */
export const useInvalidateQueries = () => {
  const queryClient = useQueryClient();

  return async (keys: string[] | string[][]) => {
    // Detect if single key or multiple keys
    const keyArray =
      typeof keys[0] === "string"
        ? [keys as string[]] // Single key: ["getRoles"] → [["getRoles"]]
        : (keys as string[][]); // Multiple keys: [["getRoles"], ["getUsers"]]

    // Invalidate all keys in parallel
    await Promise.all(
      keyArray.map((key) => queryClient.invalidateQueries({ queryKey: key }))
    );
  };
};
